import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

export default function DocumentosManager({ usuario, initialFilter = null }) {
    const [documentos, setDocumentos] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [contratistas, setContratistas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState(initialFilter || '');

    const [formData, setFormData] = useState({
        titulo: '',
        tipo_documento_id: '',
        id_contratista: '',
        archivo: null
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchData = async () => {
        try {
            const [docsResp, tiposResp, contResp] = await Promise.all([
                fetchWithAuth(`${API_BASE_URL}/api/documentos`),
                fetchWithAuth(`${API_BASE_URL}/api/tipo-documentos?empresa_id=${usuario.empresa_id}`),
                fetchWithAuth(`${API_BASE_URL}/api/contratistas?empresa_id=${usuario.empresa_id}`)
            ]);

            if (docsResp.ok) setDocumentos(await docsResp.json());
            if (tiposResp.ok) setTipos(await tiposResp.json());
            if (contResp.ok) {
                const data = await contResp.json();
                setContratistas(data);
                // Si el usuario es tipo 'C' y hay contratistas, pre-seleccionar el suyo
                if (usuario.tipo === 'C' && usuario.id_contratista) {
                    setFormData(prev => ({ ...prev, id_contratista: usuario.id_contratista }));
                }
            }
        } catch (err) {
            setError('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!formData.archivo) return alert('Selecciona un archivo');

        setUploading(true);
        const data = new FormData();
        data.append('archivo', formData.archivo);
        data.append('titulo', formData.titulo || formData.archivo.name);
        data.append('tipo_documento_id', formData.tipo_documento_id);
        if (formData.id_contratista) data.append('id_contratista', formData.id_contratista);

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/api/documentos`, {
                method: 'POST',
                body: data
            });

            if (response.ok) {
                alert('Documento subido y analizado con éxito');
                fetchData();
                setFormData({ titulo: '', tipo_documento_id: '', id_contratista: '', archivo: null });
                // Reset file input
                document.getElementById('file-input').value = '';
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Error al subir');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setUploading(false);
        }
    };

    const filteredDocumentos = statusFilter
        ? documentos.filter(doc => {
            if (statusFilter === 'pendientes') {
                return doc.validation_status === 'invalid' || doc.validation_status === 'error' || doc.validation_status === 'pending';
            }
            return true;
        })
        : documentos;

    return (
        <div className="manager-container">
            <div className="header-actions">
                <h2>Gestión de Expedientes</h2>
                {statusFilter && (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                            Filtrando por: <strong style={{ color: '#ef4444' }}>Pendientes / Inválidos</strong>
                        </span>
                        <button
                            onClick={() => setStatusFilter('')}
                            className="btn-secondary"
                            style={{ padding: '6px 14px', fontSize: '0.85rem' }}
                        >
                            ✕ Quitar filtro
                        </button>
                    </div>
                )}
            </div>

            <section className="upload-section" style={{
                backgroundColor: 'var(--bg-card)',
                padding: '32px',
                borderRadius: 'var(--radius-lg)',
                marginBottom: '40px',
                boxShadow: 'var(--shadow-md)',
                border: '1px solid var(--border-light)'
            }}>
                <h3 style={{ marginBottom: '24px', fontSize: '1.25rem' }}>📤 Cargar Nuevo Documento</h3>
                <form onSubmit={handleUpload} style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '24px',
                    alignItems: 'end'
                }}>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label>Título Informativo</label>
                        <input
                            value={formData.titulo}
                            onChange={e => setFormData({ ...formData, titulo: e.target.value })}
                            placeholder="Ej: Certificado de Antecedentes"
                        />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label>Categoría</label>
                        <select
                            value={formData.tipo_documento_id}
                            onChange={e => setFormData({ ...formData, tipo_documento_id: e.target.value })}
                            required
                        >
                            <option value="">Seleccionar tipo...</option>
                            {tipos.map(t => <option key={t.id} value={t.id}>{t.tipo_documento}</option>)}
                        </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label>Contratista Asociado</label>
                        <select
                            value={formData.id_contratista}
                            onChange={e => setFormData({ ...formData, id_contratista: e.target.value })}
                            disabled={usuario.tipo === 'C'}
                        >
                            {usuario.tipo !== 'C' && <option value="">General (Sin contratista)</option>}
                            {contratistas.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                        </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label>Archivo Digital (PDF o Imagen)</label>
                        <input
                            id="file-input"
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={e => setFormData({ ...formData, archivo: e.target.files[0] })}
                            required
                            style={{ padding: '8px' }}
                        />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <button type="submit" disabled={uploading} className="upload-button" style={{
                            width: '100%',
                            height: '46px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginTop: '0'
                        }}>
                            {uploading ? '🧬 PROCESANDO IA...' : '⚡ SUBIR Y ANALIZAR'}
                        </button>
                    </div>
                </form>
            </section>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                    <p>Sincronizando expedientes...</p>
                </div>
            ) : (
                <table className="standard-table">
                    <thead>
                        <tr>
                            <th>Nombre del Documento</th>
                            <th>Categoría</th>
                            <th>Contratista</th>
                            <th>Estado IA</th>
                            <th>Resultado del Análisis</th>
                            <th>Acción</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDocumentos.map(doc => (
                            <tr key={doc.id}>
                                <td data-label="Documento" style={{ fontWeight: '600' }}>{doc.titulo}</td>
                                <td data-label="Categoría">
                                    <span style={{
                                        backgroundColor: '#f1f5f9',
                                        padding: '4px 10px',
                                        borderRadius: '6px',
                                        fontSize: '0.8rem',
                                        color: '#475569'
                                    }}>
                                        {tipos.find(t => t.id === doc.tipo_documento_id)?.tipo_documento || 'Cat. eliminada'}
                                    </span>
                                </td>
                                <td data-label="Contratista">{contratistas.find(c => c.id === doc.id_contratista)?.nombre || '—'}</td>
                                <td data-label="Estado IA">
                                    <span className={`status-badge ${doc.validation_status}`}>
                                        {doc.validation_status}
                                    </span>
                                </td>
                                <td data-label="Resultado" style={{ fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                                    {doc.validation_details}
                                </td>
                                <td data-label="Acción">
                                    <a
                                        href={`${API_BASE_URL}/uploads/${doc.nombre_archivo}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            color: 'var(--primary)',
                                            textDecoration: 'none',
                                            fontWeight: '600',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        📂 VER DOCUMENTO
                                    </a>
                                </td>
                            </tr>
                        ))}
                        {filteredDocumentos.length === 0 && (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>
                                    No hay documentos que coincidan con el filtro.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </div>
    );
}
